import { useCallback, useEffect, useRef, useState, type MutableRefObject } from "react";
import { toast } from "sonner";
import { useConversationImageMediaSaver } from "@/hooks/voice/useConversationImageMediaSaver";
import { MAX_IMAGE_ATTACHMENTS, useImageAttachments } from "@/hooks/voice/useImageAttachments";

/**
 * Wires the in-composer image attachment picker (useImageAttachments) to the
 * per-conversation media saver (useConversationImageMediaSaver): tracks how
 * many images have already been submitted this conversation so the picker's
 * remaining-slot count stays correct, resets that count when the
 * conversation id changes, and exposes a settle-then-end helper for hanging
 * up without losing in-flight uploads.
 *
 * `endConversation` is injected rather than read from useVoiceAgentConversation
 * directly, so this hook has no dependency on the (not yet extracted)
 * conversation-core hook.
 */
export function useStudioImageAttachments(
  conversationId: string | null | undefined,
  userId: string | null | undefined,
  endConversation: () => Promise<unknown>,
  expectedEndRef?: MutableRefObject<boolean>,
) {
  const [submittedImageCount, setSubmittedImageCount] = useState(0);
  const { attachments, addFiles, removeAttachment, updateAttachmentAnalysis } = useImageAttachments(
    Math.max(0, MAX_IMAGE_ATTACHMENTS - submittedImageCount),
  );

  const imageConversationIdRef = useRef<string | null>(null);
  const {
    stageImages,
    isSaving: isSavingImages,
    pendingCount: pendingImageUploads,
    failedCount: failedImageUploads,
    retryFailed: retryFailedImageUploads,
    settleUploads: settleImageUploads,
  } = useConversationImageMediaSaver({
    conversationId,
    userId: userId ?? null,
  });

  useEffect(() => {
    if (conversationId && imageConversationIdRef.current && imageConversationIdRef.current !== conversationId) {
      setSubmittedImageCount(0);
    }
    if (conversationId) imageConversationIdRef.current = conversationId;
  }, [conversationId]);

  const endConversationAfterUploads = useCallback(async () => {
    const settled = await settleImageUploads(10_000);
    if (!settled) toast.error("Some photos are still saving. They will remain available to retry.");
    if (expectedEndRef) expectedEndRef.current = true;
    await endConversation();
    setSubmittedImageCount(0);
  }, [settleImageUploads, endConversation, expectedEndRef]);

  const handleAddImage = (source: "library" | "camera") => {
    const id = source === "camera" ? "genie-chat-camera" : "genie-chat-attachments";
    (document.getElementById(id) as HTMLInputElement | null)?.click();
  };

  return {
    attachments,
    addFiles,
    removeAttachment,
    updateAttachmentAnalysis,
    submittedImageCount,
    setSubmittedImageCount,
    stageImages,
    isSavingImages,
    pendingImageUploads,
    failedImageUploads,
    retryFailedImageUploads,
    endConversationAfterUploads,
    handleAddImage,
  };
}
